import { ReactElement, useState } from 'react';
import { Button, Checkbox, Divider, Dropdown } from 'antd';
import { IconType } from 'react-icons';
import { TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import DropdownPanel from '../DropdownPanel';

export interface DisplayedPropertyOption<Key extends string> {
    description?: string;
    icon: IconType;
    key: Key;
    label: string;
}

interface DisplayedPropertiesDropdownProps<Key extends string> {
    children: ReactElement;
    displayedProperties: readonly Key[];
    label?: string;
    onChange: (properties: Key[]) => void;
    onReset: () => void;
    options: readonly DisplayedPropertyOption<Key>[];
    propertyOrder: readonly Key[];
}

const DisplayedPropertiesDropdown = <Key extends string>({
    children,
    displayedProperties,
    label = 'Displayed Properties',
    onChange,
    onReset,
    options,
    propertyOrder,
}: DisplayedPropertiesDropdownProps<Key>): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);
    const selectedKeys = new Set(displayedProperties);
    const optionByKey = new Map<Key, DisplayedPropertyOption<Key>>(options.map((option) => [option.key, option]));

    const handleToggle = (key: Key) => {
        const nextKeys = new Set(displayedProperties);

        if (nextKeys.has(key)) {
            nextKeys.delete(key);
        } else {
            nextKeys.add(key);
        }

        onChange(propertyOrder.filter((propertyKey) => nextKeys.has(propertyKey)));
    };

    return (
        <Dropdown
            popupRender={() => (
                <DropdownPanel contentStyle={{ padding: '0.375rem' }} width='15rem'>
                    <small
                        style={{ color: token.colorTextTertiary, display: 'block', padding: '0.25rem 0.5rem 0.375rem' }}
                    >
                        {label}
                    </small>
                    {propertyOrder.map((key) => {
                        const option = optionByKey.get(key);

                        if (!option) {
                            return null;
                        }

                        const Icon = option.icon;

                        return (
                            <Button
                                block
                                key={key}
                                onClick={() => handleToggle(key)}
                                style={{ justifyContent: 'flex-start' }}
                                type='text'
                            >
                                <Checkbox checked={selectedKeys.has(key)} style={{ pointerEvents: 'none' }} />
                                <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem' }}>
                                    <Icon size='0.875rem' style={{ color: token.colorTextTertiary }} />
                                    <span>{option.label}</span>
                                </span>
                            </Button>
                        );
                    })}
                    <Divider style={{ margin: '0.375rem 0' }} />
                    <Button block onClick={onReset} style={{ justifyContent: 'flex-start' }} type='text'>
                        <TbRefresh style={{ color: token.colorTextTertiary }} />
                        Restore Defaults
                    </Button>
                </DropdownPanel>
            )}
            onOpenChange={setOpen}
            open={open}
            placement='bottomLeft'
            trigger={['click']}
        >
            {children}
        </Dropdown>
    );
};

export default DisplayedPropertiesDropdown;
